import { devLog } from '../../utils/devLog';

import React, { useState, useEffect } from 'react';
import { useAppContext } from '../../hooks/useAppContext';
import { getRoleAnalyses } from '../../utils/api';

const StatsGrid = ({ onNavigate }) => {
  const { jobs } = useAppContext();
  const [totalCVs, setTotalCVs] = useState(0);
  const [candidatesScreened, setCandidatesScreened] = useState(0);
  const [shortlisted, setShortlisted] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchAllAnalyses = async () => {
      setIsLoading(true);
      
      try {
        // Fetch analyses for all jobs
        const analysesPromises = jobs.map(job => 
          job.roleId ? getRoleAnalyses(job.roleId) : Promise.resolve({ success: false, data: [] })
        );
        
        const results = await Promise.all(analysesPromises);
        
        // Collect all candidates across all roles
        let allCandidates = [];
        results.forEach(result => {
          if (result.success && result.data) {
            allCandidates = [...allCandidates, ...result.data];
          }
        });

        // Remove duplicates by email
        const uniqueCandidates = allCandidates.reduce((acc, candidate) => {
          if (!acc.find(c => c.email === candidate.email)) {
            acc.push(candidate);
          }
          return acc;
        }, []);

        // Count total CVs (same as total candidates in database)
        const totalCVCount = uniqueCandidates.length;
        
        // Count total screened candidates (those with status "Completed" or "interview_scheduled")
        const totalScreened = uniqueCandidates.filter(candidate => {
          const status = candidate.status?.toLowerCase() || '';
          return status === 'completed' || status === 'interview_scheduled' || status.includes('interview');
        }).length;
        
        // Count shortlisted (score >= 7.0 out of 10)
        const totalShortlisted = uniqueCandidates.filter(candidate => {
          const scoreMatch = candidate.overallScore?.match(/([0-9.]+)/);
          const score = scoreMatch ? parseFloat(scoreMatch[1]) : 0;
          return score >= 7.0;
        }).length;

        setTotalCVs(totalCVCount);
        setCandidatesScreened(totalScreened);
        setShortlisted(totalShortlisted);
      } catch (error) {
        devLog.error('Error fetching dashboard stats:', error);
        setTotalCVs(0);
        setCandidatesScreened(0);
        setShortlisted(0);
      } finally {
        setIsLoading(false);
      }
    };

    if (jobs.length > 0) {
      fetchAllAnalyses();
    } else {
      setTotalCVs(0);
      setCandidatesScreened(0);
      setShortlisted(0);
      setIsLoading(false);
    }
  }, [jobs]);

  // Calculate stats
  const openJobs = jobs.filter(job => job.status === 'Open').length;

  const stats = [
    {
      number: jobs.length,
      label: 'Total Job Positions',
      sublabel: `${openJobs} active`,
      loading: false,
      tab: 'jobDefinition',
      icon: '💼'
    },
    {
      number: totalCVs,
      label: 'Total CVs Uploaded',
      sublabel: 'Across all positions',
      loading: isLoading,
      tab: 'cvUpload',
      icon: '📄'
    },
    {
      number: candidatesScreened,
      label: 'Candidates Screened',
      sublabel: 'By AI Agent',
      loading: isLoading,
      tab: 'cvUpload',
      icon: '🤖'
    },
    {
      number: shortlisted,
      label: 'Shortlisted',
      sublabel: 'Score ≥ 70%',
      loading: isLoading,
      tab: 'cvUpload',
      icon: '⭐'
    }
  ];

  return (
    <div className="stats-grid">
      {stats.map((stat, index) => (
        <div
          key={index}
          className="stat-card"
          onClick={() => onNavigate && onNavigate(stat.tab)}
          style={{
            cursor: 'pointer',
            transition: 'transform 0.15s ease, box-shadow 0.15s ease'
          }}
          onMouseEnter={e => {
            e.currentTarget.style.transform = 'translateY(-3px)';
            e.currentTarget.style.boxShadow = '0 8px 25px rgba(102,126,234,0.2)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '';
          }}
        >
          <div style={{ fontSize: '1.5rem', marginBottom: '8px' }}>{stat.icon}</div>
          <h3>
            {stat.loading ? (
              <span style={{ opacity: 0.5 }}>...</span>
            ) : (
              stat.number
            )}
          </h3>
          <p>{stat.label}</p>
          <div style={{ color: '#718096', fontSize: '0.85rem', marginTop: '5px' }}>
            {stat.sublabel}
          </div>
          <div style={{ color: '#667eea', fontSize: '0.75rem', marginTop: '8px', fontWeight: 600 }}>
            Click to view →
          </div>
        </div>
      ))}
    </div>
  );
};

export default StatsGrid;
